import React, { useEffect, useState } from 'react';
import { Button, Card, Form, ListGroup } from 'react-bootstrap';
import { PaginasService } from '../../../../service/paginas.service';
import { TestsService } from '../../../../service/tests.service';
import { Pagina, Pregunta, TipoPregunta } from '../../../../service/types/cursos.types';

interface TestBuilderProps {
  paginaId: string;
  onClose?: () => void;
}

export default function TestBuilder({ paginaId, onClose }: TestBuilderProps) {
  const [pagina, setPagina] = useState<Pagina | null>(null);
  const [enunciado, setEnunciado] = useState('');
  const [tipoPregunta, setTipoPregunta] = useState<TipoPregunta>('opcion_multiple');
  const [nuevaOpcionTexto, setNuevaOpcionTexto] = useState<Record<string, string>>({});
  const [nuevaOpcionCorrecta, setNuevaOpcionCorrecta] = useState<Record<string, boolean>>({});

  const load = async () => {
    const data = await PaginasService.findOne(paginaId);
    setPagina(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginaId]);

  const handleAddPregunta = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!enunciado.trim()) {
      alert('Ingrese el enunciado de la pregunta');
      return;
    }
    await TestsService.addPregunta(paginaId, {
      enunciado,
      tipo: tipoPregunta,
    });
    setEnunciado('');
    await load();
  };

  const handleRemovePregunta = async (pregunta: Pregunta) => {
    if (!window.confirm('¿Eliminar esta pregunta?')) return;
    await TestsService.removePregunta(pregunta.id);
    await load();
  };

  const handleAddOpcion = async (pregunta: Pregunta) => {
    const texto = (nuevaOpcionTexto[pregunta.id] ?? '').trim();
    if (!texto) {
      alert('Ingrese el texto de la opción');
      return;
    }
    const esCorrecta = !!nuevaOpcionCorrecta[pregunta.id];
    await TestsService.addOpcion(pregunta.id, { texto, esCorrecta });
    setNuevaOpcionTexto((prev) => ({ ...prev, [pregunta.id]: '' }));
    setNuevaOpcionCorrecta((prev) => ({ ...prev, [pregunta.id]: false }));
    await load();
  };

  const handleRemoveOpcion = async (opcionId: string) => {
    await TestsService.removeOpcion(opcionId);
    await load();
  };

  if (!pagina) return <p>Cargando test...</p>;

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>
          Preguntas de "{pagina.titulo}"
          {pagina.puntajeMinimo != null && (
            <span className="text-muted small ms-2">(aprueba con {pagina.puntajeMinimo}%)</span>
          )}
        </span>
        {onClose && (
          <Button size="sm" variant="outline-secondary" onClick={onClose}>
            Cerrar
          </Button>
        )}
      </Card.Header>
      <Card.Body>
        {(pagina.preguntas ?? []).map((pregunta) => (
          <Card key={pregunta.id} className="mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <strong>{pregunta.enunciado}</strong>
                  <div className="text-muted small">
                    {pregunta.tipo === 'opcion_multiple' ? 'Opción múltiple' : 'Encuesta (sin nota)'}
                  </div>
                </div>
                <Button variant="outline-danger" size="sm" onClick={() => handleRemovePregunta(pregunta)}>
                  Eliminar pregunta
                </Button>
              </div>
              <ListGroup className="mt-2">
                {(pregunta.opciones ?? []).map((opcion) => (
                  <ListGroup.Item key={opcion.id} className="d-flex justify-content-between align-items-center">
                    <span>
                      {opcion.texto}
                      {pregunta.tipo === 'opcion_multiple' && opcion.esCorrecta && (
                        <span className="badge bg-success ms-2">Correcta</span>
                      )}
                    </span>
                    <Button variant="outline-danger" size="sm" onClick={() => handleRemoveOpcion(opcion.id)}>
                      Quitar
                    </Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
              {pregunta.tipo === 'opcion_multiple' && (pregunta.opciones ?? []).some((o) => o.esCorrecta) === false && (
                <div className="text-warning small mt-1">Esta pregunta aún no tiene una opción marcada como correcta.</div>
              )}
              <div className="d-flex gap-2 mt-2 align-items-center">
                <Form.Control
                  size="sm"
                  placeholder="Nueva opción"
                  value={nuevaOpcionTexto[pregunta.id] ?? ''}
                  onChange={(e) =>
                    setNuevaOpcionTexto((prev) => ({ ...prev, [pregunta.id]: e.target.value }))
                  }
                />
                {pregunta.tipo === 'opcion_multiple' && (
                  <Form.Check
                    type="checkbox"
                    label="Correcta"
                    checked={!!nuevaOpcionCorrecta[pregunta.id]}
                    onChange={(e) =>
                      setNuevaOpcionCorrecta((prev) => ({ ...prev, [pregunta.id]: e.target.checked }))
                    }
                  />
                )}
                <Button size="sm" variant="outline-primary" onClick={() => handleAddOpcion(pregunta)}>
                  + Opción
                </Button>
              </div>
            </Card.Body>
          </Card>
        ))}

        <Form onSubmit={handleAddPregunta} className="border-top pt-3">
          <Form.Group className="mb-2">
            <Form.Label>Nueva pregunta</Form.Label>
            <Form.Control
              type="text"
              value={enunciado}
              onChange={(e) => setEnunciado(e.target.value)}
              placeholder="Enunciado de la pregunta"
            />
          </Form.Group>
          <div className="d-flex gap-2 align-items-end mb-2">
            <Form.Group>
              <Form.Label>Tipo</Form.Label>
              <Form.Select
                value={tipoPregunta}
                onChange={(e) => setTipoPregunta(e.target.value as TipoPregunta)}
              >
                <option value="opcion_multiple">Opción múltiple</option>
                <option value="encuesta">Encuesta</option>
              </Form.Select>
            </Form.Group>
          </div>
          <Button type="submit" variant="success" size="sm">
            + Agregar pregunta
          </Button>
        </Form>
      </Card.Body>
    </Card>
  );
}
